import { useId, useMemo, useState, type ReactNode } from 'react'
import { Banknote, Bell, Check, Download } from 'lucide-react'
import type { QueryKey } from '@tanstack/react-query'
import type { Invoice } from '@cattle/contracts'
import { paisaToRupees, todayIso } from '@cattle/money'

import {
  Avatar,
  BarList,
  Button,
  ConfirmDialog,
  DataTable,
  DateInput,
  EmptyState,
  Field,
  KeyValue,
  LoadingSkeleton,
  Modal,
  Money,
  MoneyInput,
  PageHeader,
  QueryBoundary,
  RowAction,
  Section,
  Select,
  StatTile,
  StatusBadge,
  TextInput,
  useToast,
  type Column,
} from '../../../components'
import {
  INVOICE_METHODS,
  queryKeys,
  usePlans,
  useSubscriptions,
  type BusinessStatus,
  type InvoiceMethod,
  type OverdueRow,
  type PlanCode,
} from '../../../fixtures'
import { useIsPlatformOwner, useServerToday } from '../../../app/session'
import { post } from '../../../lib/apiClient'
import { cn } from '../../../lib/cn'
import { downloadCsv } from '../../../lib/csv'
import { formatDateShort, formatPercent } from '../../../lib/format'
import { t, type MessageKey } from '../../../lib/i18n'
import { useApiMutation } from '../../../lib/mutations'

/**
 * P-06 Subscriptions.
 *
 * Support reads this screen; support does not move money on it. Retry, Record
 * payment and Suspend are ABSENT for a support account rather than disabled —
 * a greyed-out Suspend still tells them the platform can suspend a customer.
 * Suspend here means what it means everywhere since C10 was decided on 10
 * September 2026: the book stays readable and stops accepting entries.
 * Call and Remind belong to both roles, because chasing a payment by phone is
 * exactly the job support is for.
 *
 * ---------------------------------------------------------------------------
 * G22 — WHAT "REMIND" ACTUALLY DOES
 * ---------------------------------------------------------------------------
 * Nothing is delivered. `POST /platform/invoices/:id/remind` writes an audit
 * row and stamps the invoice, and the only `SupportNotifier` there is answers
 * `{ delivered: false, reason: 'NO_PROVIDER' }` — no WhatsApp provider, no
 * approved template, no consent model. So every toast on this screen says the
 * reminder was *recorded*. Telling the reader it was sent would have him stop
 * chasing a man who was never contacted.
 */

/**
 * P8's answer. `businessStatusResponse` has a schema in the frozen contract but
 * no exported type beside it (the same omission as C3), so the two fields this
 * screen would ever read are named here rather than the response being untyped.
 */
type SuspendResult = { id: string; status: BusinessStatus }

/** Late enough to worry, late enough to act. The word is always spelled out. */
function lateTone(days: number): string {
  if (days >= 14) return 'text-danger'
  if (days >= 7) return 'text-warn'
  return 'text-dark'
}

/**
 * `INVOICE_METHODS` is the contract's closed set, and every label is looked up
 * — the code is what travels. Three of the four words already live in the
 * shared dictionary the phone renders from; `CARD` is not a `PAYMENT_METHOD`
 * and has the console's own label.
 */
const METHOD_LABELS: Record<InvoiceMethod, MessageKey> = {
  JAZZCASH: 'paymentMethod.JAZZCASH',
  EASYPAISA: 'paymentMethod.EASYPAISA',
  BANK: 'paymentMethod.BANK',
  CARD: 'paymentMethod.CARD',
}

export function SubscriptionsScreen() {
  const subs = useSubscriptions()
  const plans = usePlans()
  const isOwner = useIsPlatformOwner()
  const today = useServerToday()
  const toast = useToast()

  const methodId = useId()
  const paidOnId = useId()
  const amountId = useId()
  const referenceId = useId()
  const suspendReasonId = useId()

  /* The row a write is about outlives the sheet it was started from: the
     invalidation list below is read when the request comes back, and by then
     the dialog has closed itself. Losing the slug there would leave that
     business's own page showing the figure from before the payment. */
  const [suspending, setSuspending] = useState<OverdueRow | null>(null)
  const [suspendOpen, setSuspendOpen] = useState(false)
  const [suspendReason, setSuspendReason] = useState('')
  const [recording, setRecording] = useState<OverdueRow | null>(null)
  const [recordOpen, setRecordOpen] = useState(false)
  const [recordPaisa, setRecordPaisa] = useState<number | undefined>(undefined)
  const [recordMethod, setRecordMethod] = useState<InvoiceMethod>('JAZZCASH')
  const [recordPaidOn, setRecordPaidOn] = useState('')
  const [recordReference, setRecordReference] = useState('')

  /* A plan name is data, not copy — the owner can rename or add one — so the
     label is resolved from the plans the platform actually sells. */
  const planNames = useMemo(
    () => new Map((plans.data ?? []).map((plan) => [plan.code, plan.name] as const)),
    [plans.data],
  )
  const planName = (code: PlanCode): string => planNames.get(code) ?? code

  const overdueCount = subs.data?.overdueCount ?? 0
  const overdueRows = useMemo(() => subs.data?.overdue ?? [], [subs.data])

  /**
   * What a settled invoice moves, and therefore what has to be refetched.
   *
   * A payment does not only clear a row in this table: the subscription
   * changes, the business walks back out of PAST_DUE, and the overview's
   * revenue tile is a different number. Anything left uninvalidated keeps
   * rendering the figure from before the write, which on this screen means a
   * business that has paid still being chased.
   */
  const settlementKeys = (row: OverdueRow | null): QueryKey[] => [
    queryKeys.subscriptions,
    queryKeys.businesses,
    queryKeys.platformOverview,
    ...(row ? [queryKeys.businessDetail(row.slug), queryKeys.invoices(row.slug)] : []),
  ]

  const recordPayment = useApiMutation<OverdueRow, Invoice>({
    run: (row) =>
      post<Invoice>(`/platform/invoices/${row.invoiceId}/record-payment`, {
        method: recordMethod,
        paidOn: recordPaidOn,
        // The figure that actually arrived — a part payment is recorded as what
        // came in, never rounded up to the invoice. Never a 0 stood in for a
        // blank: the field is required before the button enables.
        amountPaisa: recordPaisa,
        ...(recordReference.trim() ? { reference: recordReference.trim() } : {}),
      }),
    invalidates: settlementKeys(recording),
    success: (_invoice, row) => t('p.subs.toastRecorded', { name: row.name }),
    onDone: () => setRecordOpen(false),
  })

  const retryCharge = useApiMutation<OverdueRow, Invoice>({
    run: (row) => post<Invoice>(`/platform/invoices/${row.invoiceId}/retry`),
    invalidates: [queryKeys.subscriptions, queryKeys.businesses, queryKeys.platformOverview],
    success: (_invoice, row) => t('p.subs.toastRetry', { name: row.name }),
  })

  const remind = useApiMutation<OverdueRow, Invoice>({
    run: (row) =>
      post<Invoice>(`/platform/invoices/${row.invoiceId}/remind`, { channel: 'WHATSAPP' }),
    invalidates: [queryKeys.subscriptions],
    success: (_invoice, row) => t('p.subs.toastRemind', { name: row.name }),
  })

  /**
   * Chase all — the same reminder, once per overdue invoice.
   *
   * There is no bulk route in the contract and inventing one client-side would
   * hide the interesting case, which is a partial result: some recorded, some
   * refused. `allSettled` keeps every refusal from cancelling the rest, and the
   * toast says how many actually landed. Only a clean sweep of failures throws,
   * so the server's own words reach the reader instead of a count of nothing.
   */
  const chaseAll = useApiMutation<OverdueRow[], { done: number; failed: number }>({
    run: async (rows) => {
      const results = await Promise.allSettled(
        rows.map((row) =>
          post<Invoice>(`/platform/invoices/${row.invoiceId}/remind`, { channel: 'WHATSAPP' }),
        ),
      )
      const done = results.filter((result) => result.status === 'fulfilled').length
      if (done === 0) {
        for (const result of results) {
          if (result.status === 'rejected') throw result.reason
        }
      }
      return { done, failed: results.length - done }
    },
    invalidates: [queryKeys.subscriptions, queryKeys.platformOverview],
    success: (result, rows) =>
      result.failed === 0
        ? t('p.subs.toastChased', { count: result.done })
        : t('p.subs.toastChasedPartial', { done: result.done, count: rows.length }),
  })

  /**
   * Freezing a book, which is now the ONLY severity there is.
   *
   * C10 was decided on 10 September 2026: **suspension means read-only, not
   * lockout.** So the word Suspend no longer promises a severity the server
   * does not have, and this screen and P-03's danger zone post to the same
   * route with the same copy — which is the whole of what the two screens used
   * to disagree about. `…/read-only` is the same act under the status dunning
   * uses; nothing in this console calls it by hand.
   */
  const suspendBusiness = useApiMutation<{ row: OverdueRow; reason: string }, SuspendResult>({
    run: ({ row, reason }) =>
      post<SuspendResult>(`/platform/businesses/${row.businessId}/suspend`, { reason }),
    invalidates: settlementKeys(suspending),
    success: (_status, { row }) => t('p.business.suspendToast', { name: row.name }),
    onDone: () => setSuspendReason(''),
  })

  const openRecord = (row: OverdueRow) => {
    setRecordPaisa(row.amountPaisa)
    setRecordMethod('JAZZCASH')
    // The server's day in Asia/Karachi, never the browser's — a payment
    // recorded at 2am Karachi must not be dated yesterday.
    setRecordPaidOn(today ?? '')
    setRecordReference('')
    setRecording(row)
    setRecordOpen(true)
  }

  const openSuspend = (row: OverdueRow) => {
    setSuspendReason('')
    setSuspending(row)
    setSuspendOpen(true)
  }

  /*
    THE STATEMENT.

    CONTRACT GAP: there is no statement route. Nothing in the contract produces
    a subscriptions statement — not a PDF, not a CSV, not a job to poll. So the
    file is written here, in the browser, from the rows this screen is already
    showing, and the toast says exactly that. "Your statement is being prepared"
    described a server that was never asked for anything, and left whoever
    pressed it waiting for a download that was never coming.

    TWO THINGS THIS HAD TO GET RIGHT

    1. **An absent figure is an EMPTY CELL, never 0.** `amountPaisa` is optional
       because the server strips money from the payload for an actor without
       `platform.billing` — the key is missing, not zeroed. A `0` in a
       spreadsheet column headed "Amount" is a claim about somebody's money, and
       it is a claim that outlives the session in a file. A second toast says so
       when anything was actually blank, because by then the reader may already
       be forwarding the file.

    2. **The rows are the ones on screen.** Overdue is sorted the way the table
       sorts it by default (latest first), and the renewals section carries the
       "{count} more" aggregate the panel itself shows — dropping it would make
       the file's total disagree with the figure the reader is looking at.

    Quoting and the BOM belong to `lib/csv.ts`; both are load-bearing there.
  */
  const exportStatement = () => {
    const summary = subs.data
    const overdue = [...(summary?.overdue ?? [])].sort((a, b) => b.lateDays - a.lateDays)
    const renewals = summary?.renewals ?? []
    const more = summary?.moreRenewals
    const hasMore = more !== undefined && more.count > 0

    /** Rupees, or nothing at all. `undefined` must not become a figure. */
    const money = (paisa: number | undefined): string =>
      paisa === undefined ? '' : String(paisaToRupees(paisa))

    const rows: (string | number | undefined | null)[][] = [
      ...overdue.map((row) => [
        t('p.subs.csvOverdue'),
        row.name,
        row.city,
        planName(row.plan),
        money(row.amountPaisa),
        row.lateDays,
        row.lastTried,
        '',
      ]),
      ...renewals.map((renewal) => [
        t('p.subs.csvRenewing'),
        renewal.name,
        '',
        '',
        money(renewal.amountPaisa),
        '',
        '',
        renewal.date,
      ]),
      ...(hasMore
        ? [
            [
              t('p.subs.csvRenewing'),
              t('p.subs.moreRenewals', { count: more.count }),
              '',
              '',
              money(more.amountPaisa),
              '',
              '',
              '',
            ],
          ]
        : []),
    ]

    if (rows.length === 0) {
      toast.show(t('p.subs.statementEmpty'), 'info')
      return
    }

    downloadCsv(
      // Two exports of a moving list are two different files, so the day is in
      // the name — the server's day in Asia/Karachi, never the browser's.
      `${t('p.subs.statementFilename')}-${today ?? todayIso()}`,
      [
        t('p.subs.csvSection'),
        t('label.business'),
        t('p.businesses.cityLabel'),
        t('label.plan'),
        t('label.amount'),
        t('p.subs.colLateBy'),
        t('p.subs.colLastTried'),
        t('p.subs.csvRenewsOn'),
      ],
      rows,
    )

    toast.show(t('p.subs.toastStatement', { count: rows.length }))

    const anyHidden =
      overdue.some((row) => row.amountPaisa === undefined) ||
      renewals.some((renewal) => renewal.amountPaisa === undefined) ||
      (hasMore && more.amountPaisa === undefined)
    if (anyHidden) toast.show(t('p.subs.statementLocked'), 'info')
  }

  const doCell = (row: OverdueRow): ReactNode => {
    const call = (
      <Button
        key="call"
        size="sm"
        onClick={() => toast.show(t('p.subs.toastCall', { name: row.name }), 'info')}
      >
        {t('act.call')}
      </Button>
    )
    /*
     * Owner only, and the reason is worth stating because the two sources
     * disagree: plan.md §6.1 P-06 says "Call/Remind — both", but the FROZEN
     * contract row for `POST /platform/invoices/:id/remind` is
     * `role: 'PLATFORM_OWNER'`, and the API enforces that. Rendering it for
     * support would be a control that can only ever fail — and this console
     * gates by ABSENCE, not by letting the server refuse. The contradiction is
     * logged in `contract-gaps.md` for a ruling; until then the contract wins.
     */
    const remindButton = !isOwner ? null : (
      <Button
        key="remind"
        size="sm"
        disabled={remind.isPending || chaseAll.isPending}
        onClick={() => remind.mutate(row)}
      >
        {t('act.remind')}
      </Button>
    )
    /* Money-moving. Owner only, and absent — not disabled — for support. */
    const retry = isOwner ? (
      <Button
        key="retry"
        size="sm"
        disabled={retryCharge.isPending}
        onClick={() => retryCharge.mutate(row)}
      >
        {t('act.retry')}
      </Button>
    ) : null
    const suspend = isOwner ? (
      <Button key="restrict" size="sm" variant="danger" onClick={() => openSuspend(row)}>
        {t('p.business.suspendAction')}
      </Button>
    ) : null

    const buttons = (
      row.action === 'CALL_SUSPEND'
        ? [call, suspend]
        : row.action === 'CALL_REMIND'
          ? [call, remindButton]
          : row.action === 'RETRY_REMIND'
            ? [retry, remindButton]
            : [
                <Button
                  key="leave"
                  size="sm"
                  variant="ghost"
                  onClick={() => toast.show(t('p.subs.toastLeave', { name: row.name }), 'info')}
                >
                  {t('p.subs.leaveIt')}
                </Button>,
              ]
    ).filter(Boolean)

    return <div className="flex justify-end gap-1.5">{buttons}</div>
  }

  const columns: Column<OverdueRow>[] = [
    {
      id: 'business',
      header: t('label.business'),
      sortBy: (row) => row.name,
      cell: (row) => (
        <div className="flex items-center gap-2.5">
          <Avatar name={row.name} />
          <span className="min-w-0">
            <b className="block truncate font-display text-[13.5px] font-bold text-dark">
              {row.name}
            </b>
            <span className="block text-[11.5px] text-muted">
              {t('p.subs.cityAnimals', { city: row.city, count: row.animals })}
            </span>
          </span>
        </div>
      ),
    },
    {
      id: 'plan',
      header: t('label.plan'),
      cell: (row) => <StatusBadge tone="neutral">{planName(row.plan)}</StatusBadge>,
    },
    {
      id: 'amount',
      header: t('label.amount'),
      numeric: true,
      sortBy: (row) => row.amountPaisa ?? 0,
      cell: (row) => <Money paisa={row.amountPaisa} />,
    },
    {
      id: 'late',
      header: t('p.subs.colLateBy'),
      numeric: true,
      sortBy: (row) => row.lateDays,
      cell: (row) => (
        <span className={cn('font-display text-[13px] font-bold tnum', lateTone(row.lateDays))}>
          {t('p.subs.lateDays', { count: row.lateDays })}
        </span>
      ),
    },
    {
      id: 'lastTried',
      header: t('p.subs.colLastTried'),
      secondary: true,
      cell: (row) => <span className="text-[12.5px] text-sub">{row.lastTried}</span>,
    },
    {
      id: 'do',
      header: t('label.do'),
      numeric: true,
      cell: doCell,
    },
  ]

  return (
    <>
      <PageHeader
        title={t('p.subs.title')}
        subtitle={t('p.subs.subtitle')}
        actions={
          <>
            {/* Written in the browser from the rows on screen — see
                `exportStatement`. Not gated: it exports the payload the reader
                already has, money and all, which for a support account is a
                payload with the money absent. */}
            <Button icon={<Download className="size-4" />} onClick={exportStatement}>
              {t('p.subs.statement')}
            </Button>
            {/* Fans out one remind per invoice, so it inherits remind's
                owner-only rule rather than failing once per row. */}
            {isOwner ? (
              <Button
                variant="primary"
                icon={<Bell className="size-4" />}
                disabled={overdueCount === 0 || chaseAll.isPending || remind.isPending}
                onClick={() => chaseAll.mutate(overdueRows)}
              >
                {t('p.subs.chaseAll')}
              </Button>
            ) : null}
          </>
        }
      />

      <QueryBoundary
        query={subs}
        skeleton={
          <>
            <LoadingSkeleton variant="tiles" className="mb-3.5" />
            <LoadingSkeleton variant="table" rows={4} />
          </>
        }
      >
        {(data) => (
          <>
            <div className="mb-3.5 grid gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
              <StatTile
                surface="heavy"
                label={t('p.subs.collected')}
                value={<Money paisa={data.collectedPaisa} />}
                sub={t('p.subs.collectedSub', { paid: data.paidCount, total: data.payingCount })}
              />
              <StatTile
                surface="heavy"
                label={t('p.subs.overdue')}
                value={<Money paisa={data.overduePaisa} tone="warn" />}
                sub={t('p.subs.overdueSub', {
                  count: data.overdueCount,
                  share: formatPercent(data.overdueShare, 1),
                })}
              />
              <StatTile
                surface="heavy"
                // The same figures the "Renewing this week" section below is
                // built from — so the tile names the same window, rather than a
                // 7-day one the payload never mentions.
                label={t('p.subs.renewingThisWeek')}
                value={<Money paisa={data.renewingPaisa} />}
                sub={t('p.subs.renewsSub', { count: data.renewingCount })}
              />
              <StatTile
                surface="heavy"
                label={t('p.subs.trialsEnding')}
                value={<span className="text-warn">{data.trialsEnding}</span>}
                sub={t('p.subs.trialsEndingSub')}
              />
            </div>

            <Section
              className="mb-3.5"
              title={t('p.subs.notPaid')}
              action={
                data.overdueCount > 0 ? (
                  <StatusBadge tone="danger">
                    {t('p.subs.notPaidSub', { count: data.overdueCount })}
                  </StatusBadge>
                ) : null
              }
            >
              {/* Scoped to the table: when nobody is overdue the rest of the
                  screen — renewals, failure reasons — still has to render. */}
              <QueryBoundary
                query={subs}
                skeleton={<LoadingSkeleton variant="table" rows={4} />}
                isEmpty={(summary) => summary.overdue.length === 0}
                empty={
                  <EmptyState
                    icon={<Check className="size-6" />}
                    title={t('p.subs.emptyTitle')}
                    body={t('p.subs.emptyBody')}
                  />
                }
              >
                {(summary) => (
                  <>
                    <DataTable
                      columns={columns}
                      rows={summary.overdue}
                      rowKey={(row) => row.businessId}
                      caption={t('p.subs.notPaid')}
                      defaultSort={{ columnId: 'late', direction: 'desc' }}
                      rowActions={
                        isOwner
                          ? (row) => (
                              <RowAction
                                label={t('p.subs.recordPayment')}
                                icon={<Banknote className="size-full" />}
                                onClick={() => openRecord(row)}
                              />
                            )
                          : undefined
                      }
                    />
                    <p className="mt-3.5 rounded-r border border-accent-glass-border bg-accent-glass p-3.5 text-[12.5px] leading-relaxed text-sub">
                      {t('p.subs.seasonNote')}
                    </p>
                  </>
                )}
              </QueryBoundary>
            </Section>

            <div className="grid gap-3.5 lg:grid-cols-2">
              <Section title={t('p.subs.renewingThisWeek')}>
                {data.renewals.map((renewal) => (
                  <KeyValue
                    key={`${renewal.date}-${renewal.name}`}
                    label={`${formatDateShort(renewal.date)} · ${renewal.name}`}
                    value={<Money paisa={renewal.amountPaisa} />}
                  />
                ))}
                <KeyValue
                  label={
                    <span className="text-muted">
                      {t('p.subs.moreRenewals', { count: data.moreRenewals.count })}
                    </span>
                  }
                  value={<Money paisa={data.moreRenewals.amountPaisa} className="text-sub" />}
                />
              </Section>

              <Section title={t('p.subs.whyFail')}>
                <BarList
                  items={data.failureReasons.map((reason) => ({
                    id: reason.reason,
                    label: reason.reason,
                    value: reason.count,
                    display: t('p.subs.countOf', { count: reason.count, of: reason.of }),
                    tone: 'warn' as const,
                  }))}
                  max={data.failureReasons[0]?.of}
                />
                <p className="mt-3.5 border-t border-muted/15 pt-3.5 text-[12.5px] leading-relaxed text-sub">
                  {t('p.subs.failNote')}
                </p>
              </Section>
            </div>
          </>
        )}
      </QueryBoundary>

      {/* Suspension freezes a book a trader is trading out of today — it does
          not close it, and since C10 neither the body nor the consequence line
          below is allowed to say otherwise. The name has to be typed, and the
          reason is written down because P8 records who did this and why. */}
      <ConfirmDialog
        open={suspendOpen}
        onClose={() => setSuspendOpen(false)}
        onConfirm={() => {
          if (suspending) suspendBusiness.mutate({ row: suspending, reason: suspendReason.trim() })
        }}
        title={suspending ? t('p.business.suspendTitle', { name: suspending.name }) : ''}
        body={t('p.subs.seasonNote')}
        consequence={
          suspending ? t('p.subs.restrictMidSeason', { name: suspending.name }) : undefined
        }
        confirmLabel={t('p.business.suspendAction')}
        tone="danger"
        typeToConfirm={suspending?.name}
        disabled={suspendReason.trim() === '' || suspendBusiness.isPending}
      >
        <Field
          label={t('p.business.suspendReason')}
          hint={t('p.subs.restrictReasonHint')}
          htmlFor={suspendReasonId}
        >
          <TextInput
            id={suspendReasonId}
            value={suspendReason}
            onChange={setSuspendReason}
            className="max-w-none"
          />
        </Field>
      </ConfirmDialog>

      <Modal
        open={recordOpen}
        onClose={() => setRecordOpen(false)}
        title={t('p.subs.recordPayment')}
        subtitle={recording?.name}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRecordOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              disabled={
                recordPaisa === undefined || recordPaidOn === '' || recordPayment.isPending
              }
              onClick={() => {
                if (recording) recordPayment.mutate(recording)
              }}
            >
              {t('act.saveChanges')}
            </Button>
          </>
        }
      >
        <Field label={t('p.subs.recordMethod')} htmlFor={methodId}>
          <Select<InvoiceMethod>
            id={methodId}
            value={recordMethod}
            onChange={setRecordMethod}
            options={INVOICE_METHODS.map((method) => ({
              value: method,
              label: t(METHOD_LABELS[method]),
            }))}
            ariaLabel={t('p.subs.recordMethod')}
          />
        </Field>
        <Field label={t('p.subs.recordPaidOn')} htmlFor={paidOnId}>
          <DateInput id={paidOnId} value={recordPaidOn} onChange={setRecordPaidOn} />
        </Field>
        <Field
          label={t('label.amount')}
          hint={t('p.subs.recordAmountHint')}
          htmlFor={amountId}
        >
          <MoneyInput id={amountId} paisa={recordPaisa} onChange={setRecordPaisa} />
        </Field>
        <Field
          label={t('p.subs.recordReference')}
          hint={t('p.subs.recordReferenceHint')}
          htmlFor={referenceId}
        >
          <TextInput id={referenceId} value={recordReference} onChange={setRecordReference} />
        </Field>
      </Modal>
    </>
  )
}
